import React from 'react';
import {Form} from 'react-final-form';
import {useIntl} from 'react-intl';
import cn from 'classnames';

import {EEventSource} from 'coreClasses/events/IEvents';
import {useCustomerMgr, useNotify} from 'coreHooks';
import validationRules from 'coreUtils/validationRules';

import Button from 'brandComponents/Button';
import CheckboxField from 'brandComponents/Form/Subcomponents/CheckboxField';
import Field from 'brandComponents/Form/Subcomponents/Field';
import Icon from 'brandComponents/Icon';
import Link from 'brandComponents/Link';
import {NEWSLETTER_SOURCE} from 'brandConstants';

import {IFooterNewsletterForm, IRegistrationValidationErrors} from './IFooterNewsletterForm';
import messages from './messages';

import styles from './FooterNewsletter.module.scss';

const FooterNewsletter = ({classNames}: IFooterNewsletterForm) => {
	const intl = useIntl();
	const customerMgr = useCustomerMgr();
	const notify = useNotify();
	const nlSubscriptionSrc: string = EEventSource[EEventSource.NEWSLETTER_POPUP];

	const initialValues = {
		email: '',
		privacy: false,
		profiling: false,
	};

	const handleSubscribe = async (value: Record<string, any>) => {
		const {gender, email, privacy, profiling} = value;

		const newsletterSource = {
			newsletterSource: NEWSLETTER_SOURCE[nlSubscriptionSrc],
			newsletterSourceDetails: {
				pageType: NEWSLETTER_SOURCE.CUSTOM_POPUP,
			}
		};

		try {
			const {subscriptionCode} = await customerMgr.subscribeToNewsletter(
				email,
				gender,
				NEWSLETTER_SOURCE[nlSubscriptionSrc],
				newsletterSource,
				[],
				profiling,
				privacy
			);

			switch (subscriptionCode) {
				case 'OK':
					notify.success(intl.formatMessage(messages.signInSuccess));

					break;
				default:
					throw new Error();
			}
		} catch (e) {
			notify.error(intl.formatMessage(messages.signInFailure));
		}
	};

	return (
		<Form
			onSubmit={handleSubscribe}
			initialValues={initialValues}
			validate={(values) => {
				const validationErrors: IRegistrationValidationErrors = {};

				if (!values.email) {
					validationErrors.email = intl.formatMessage(messages.signInEmailError);
				}

				if (!values?.privacy) {
					validationErrors.privacy = intl.formatMessage(messages.mandatory);
				}

				return validationErrors;
			}}
			render={({handleSubmit, hasValidationErrors}) => {
				return (
					<form className={cn(classNames, styles['footer-newsletter__form'])} onSubmit={handleSubmit}>
						<Field
							name="email"
							type="email"
							id="newsletter-email"
							component="input"
							label={intl.formatMessage(messages.signInEmailPlaceholder)}
							validation={[
								{
									rule: validationRules.isNotEmpty,
									errorMessage: intl.formatMessage(messages.signInEmailError),
								},
								{
									rule: validationRules.isEmail,
									errorMessage: intl.formatMessage(messages.invalidEmail),
								},
							]}
						/>
						<div className={styles['footer-newsletter__privacy-policy-container']}>
							<CheckboxField
								name="privacy"
								label={intl.formatMessage(messages.signInPrivacyPolicy, {
									privacy: (chunk) => <Link to="/privacy-policy">{chunk}</Link>,
								})}
								classNames={styles['footer-newsletter__privacy-policy']}
							/>

							<CheckboxField name="profiling" label={intl.formatMessage(messages.checkboxPrivacy)} />
						</div>
						<Button
							type="submit"
							classNames={styles['footer-newsletter__cta']}
							variant="anything"
							onClick={!hasValidationErrors ? handleSubmit : undefined}
						>
							<Icon name="arrow-right" title="prev" width={16} height={16} />

						</Button>
					</form>
				);
			}}
		/>
	);
};

export default FooterNewsletter;
